import React, { useRef, useState, useEffect } from "react";
import {
    Box,
    Grid,
    Typography,
    Button,
    Divider,
    Autocomplete,
    TextField,
} from "@mui/material";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import CloseIcon from "@mui/icons-material/Close";
import { getProject } from "../../services/project.service";
import { getUser } from "../../services/user.service";
import { ToastError, ToastSuccess } from "../../utils/Toaster/Toaster";
import { postTodoTask, updateTodoTask } from "../../services/todoTask";
import { jwtDecode } from "jwt-decode";
import { useAuth } from "../../context/AuthProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import { TODO_TASK_STATUS } from "../../common/constant/constant";



const AddTodoTask = (props) => {
    const { setOpenDrawer, formType, selectedData, GetData } = props;
    const disabled = formType === "view";
    const { accessToken } = useAuth();
    const formRef = useRef(null);
    const [projectData, setProjectData] = useState([]);
    const [userData, setUserData] = useState([]);

    const [initialValues, setInitialValues] = useState({
        title: "",
        description: "",
        project: "",
        assignedTo: "",
        completionDate: null,
        status: TODO_TASK_STATUS.PENDING,
        priority: 0, // Default Low
    });

    const TODO_TASK_PRIORITY = {
        LOW: 0,
        MEDIUM: 1,
        HIGH: 2,
    };

    const priorityOptions = [
        { label: "Low", value: TODO_TASK_PRIORITY.LOW },
        { label: "Medium", value: TODO_TASK_PRIORITY.MEDIUM },
        { label: "High", value: TODO_TASK_PRIORITY.HIGH },
    ];

    const validationSchema = Yup.object().shape({
        title: Yup.string().required("Title is required"),
        // description: Yup.string().required("Description is required"),
        project: Yup.string().required("Project is required"),
        assignedTo: Yup.string().required("Assigned To is required"),
        completionDate: Yup.date()
            .required("Completion Date is required")
            .test(
                'not-in-past',
                'Completion Date cannot be in the past',
                function (value) {
                    if (!value) return true;
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    const selectedDate = new Date(value);
                    selectedDate.setHours(0, 0, 0, 0);
                    return selectedDate >= today;
                }
            )
    });

    const statusOptions = [
        { label: "Pending", value: TODO_TASK_STATUS.PENDING },
        { label: "Completed", value: TODO_TASK_STATUS.COMPLETED },
        { label: "Canceled", value: TODO_TASK_STATUS.CANCELED },
        { label: "Acknowledge", value: TODO_TASK_STATUS.ACKNOWLEDGE },
    ];

    const getUserIdFromToken = () => {
        if (!accessToken) return null;
        try {
            const decoded = jwtDecode(accessToken);
            return decoded.id;
        } catch (error) {
            console.error("Invalid token", error);
            return null;
        }
    };

    const GetProjectData = async () => {
        try {
            const res = await getProject({ isEnabled: true });
            if (res.data.data && Array.isArray(res.data.data)) {
                const configData = res.data.data.map((e) => ({
                    ...e,
                    label: e.name,
                }));
                setProjectData(configData);
            }
        } catch (error) {
            console.error("Error fetching project data:", error);
        }
    };

    const GetUserData = async () => {
        try {
            const res = await getUser({ isEnabled: true, isAdmin: true });
            if (res.data.data && Array.isArray(res.data.data)) {
                const configData = res.data.data.map((e) => ({
                    ...e,
                    label: e.name,
                }));
                setUserData(configData);
            }
        } catch (error) {
            console.error("Error fetching user data:", error);
        }
    };

    useEffect(() => {
        GetProjectData();
        GetUserData();

        if (formType === "edit" || formType === "view") {
            setInitialValues({
                title: selectedData.title || "",
                description: selectedData.description || "",
                project: selectedData.project || "",
                assignedTo: selectedData.assignedTo || "",
                completionDate: selectedData.completionDate
                    ? dayjs(selectedData.completionDate)
                    : null,
                status: selectedData.status ?? TODO_TASK_STATUS.PENDING,
                priority: selectedData.priority ?? 0,
                priorityLevel: selectedData.priorityLevel || "",
            });
        }
    }, [formType, selectedData]);

    const onSubmit = async (values, { resetForm }) => {
        try {
            const formData = new FormData();
            const userId = getUserIdFromToken();

            const bodyData = {
                title: values.title,
                description: values.description,
                project: values.project,
                assignedTo: values.assignedTo,
                completionDate: values.completionDate
                    ? dayjs(values.completionDate).toISOString()
                    : null,
                status: values.status,
                priority: values.priority,
                priorityLevel: values.priorityLevel,
                createdBy: userId,
            };

            formData.append("bodyData", JSON.stringify(bodyData));

            const apiMethod =
                formType === "edit"
                    ? () => updateTodoTask(selectedData?._id, formData)
                    : () => postTodoTask(formData);

            const res = await apiMethod();
            ToastSuccess(res.data.message);

            resetForm();
            GetData();
            setOpenDrawer(false);
        } catch (error) {
            ToastError(error?.response?.data?.message || "An error occurred");
            console.error("Submission error:", error);
        }
    };

    return (
        <div className="addRenderingPopup">
            <Box className="addContainerHeader">
                <Typography className="headerTitle">
                    {formType === "edit" ? "Edit" : formType === "view" ? "View" : "Add"}{" "}
                    TodoTask
                </Typography>
                <CloseIcon
                    className="Close-Icon"
                    onClick={() => setOpenDrawer(false)}
                />
            </Box>
            <Divider sx={{ opacity: 1 }} />
            <Formik
                initialValues={initialValues}
                enableReinitialize
                validationSchema={validationSchema}
                onSubmit={onSubmit}
                innerRef={formRef}
            >
                {({ touched, errors, values, setFieldValue }) => (
                    <Form>
                        <Box className="addContainerCenter" mb={1}>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6} mt={1}>
                                    <Typography className="inputLabel">
                                        Title <span style={{ color: "red" }}>*</span>
                                    </Typography>
                                    <Field
                                        name="title"
                                        className="InputField"
                                        placeholder="Enter Title"
                                        disabled={disabled}
                                    />
                                    <div className="form-error">
                                        {touched.title && errors.title && errors.title}
                                    </div>
                                </Grid>

                                <Grid item xs={12} sm={6} mt={1}>
                                    <Typography className="inputLabel">
                                        Description
                                    </Typography>
                                    <Field
                                        name="description"
                                        className="InputField"
                                        placeholder="Enter Description"
                                        disabled={disabled}
                                    />
                                    <div className="form-error">
                                        {touched.description &&
                                            errors.description &&
                                            errors.description}
                                    </div>
                                </Grid>

                                <Grid item xs={12} sm={6} mt={1}>
                                    <div className="wsh-form-control">
                                        <Typography className="inputLabel">
                                            Project <span style={{ color: "red" }}>*</span>
                                        </Typography>
                                        <Autocomplete
                                            options={projectData}
                                            value={
                                                projectData.find(
                                                    (option) => option?._id === values.project
                                                ) || null
                                            }
                                            disabled={disabled}
                                            onChange={(e, v) => {
                                                setFieldValue("project", v?._id);
                                            }}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    focused={false}
                                                    placeholder="Select Project"
                                                    className="AutoComplete_InputBox"
                                                />
                                            )}
                                        />
                                        <div className="form-error">
                                            {touched.project && errors.project && errors.project}
                                        </div>
                                    </div>
                                </Grid>

                                <Grid item xs={12} sm={6} mt={1}>
                                    <div className="wsh-form-control">
                                        <Typography className="inputLabel">
                                            Assigned To <span style={{ color: "red" }}>*</span>
                                        </Typography>
                                        <Autocomplete
                                            options={userData}
                                            value={
                                                userData.find(
                                                    (option) => option?._id === values.assignedTo
                                                ) || null
                                            }
                                            disabled={disabled}
                                            onChange={(e, v) => {
                                                setFieldValue("assignedTo", v?._id);
                                            }}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    focused={false}
                                                    placeholder="Select User"
                                                    className="AutoComplete_InputBox"
                                                />
                                            )}
                                        />
                                        <div className="form-error">
                                            {touched.assignedTo &&
                                                errors.assignedTo &&
                                                errors.assignedTo}
                                        </div>
                                    </div>
                                </Grid>

                                <Grid item xs={12} sm={6} mt={1}>
                                    <Typography className="inputLabel">
                                        Completion Date <span style={{ color: "red" }}>*</span>
                                    </Typography>
                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                        <DatePicker
                                            value={values.completionDate}
                                            onChange={(newValue) => {
                                                setFieldValue("completionDate", newValue);
                                            }}
                                            disabled={disabled}
                                            format="DD/MM/YYYY"
                                            slotProps={{
                                                textField: {
                                                    size: "small",
                                                    placeholder: "Select Date",
                                                    fullWidth: true,
                                                    sx: {
                                                        "& .MuiOutlinedInput-root": {
                                                            height: "40px",
                                                            borderRadius: "8px",
                                                            backgroundColor: "#fff",
                                                            "& fieldset": {
                                                                borderColor: "#E5E9F1",
                                                            },
                                                            "&:hover fieldset": {
                                                                borderColor: "#6366f1",
                                                            },
                                                            "&.Mui-focused fieldset": {
                                                                borderColor: "#6366f1",
                                                            },
                                                        },
                                                        "& .MuiInputBase-input": {
                                                            padding: "8px 14px",
                                                            fontSize: "14px",
                                                        },
                                                    },
                                                },
                                            }}
                                        />
                                    </LocalizationProvider>
                                    <div className="form-error">
                                        {touched.completionDate &&
                                            errors.completionDate &&
                                            errors.completionDate}
                                    </div>
                                </Grid>
                                {formType !== "add" && (
                                    <Grid item xs={12} sm={6} mt={1}>
                                        <div className="wsh-form-control">
                                            <Typography className="inputLabel">Status</Typography>
                                            <Autocomplete
                                                options={statusOptions}
                                                value={
                                                    statusOptions.find(
                                                        (option) => option.value === values.status
                                                    ) || null
                                                }
                                                disabled={disabled}
                                                onChange={(e, v) => {
                                                    setFieldValue("status", v?.value);
                                                }}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        focused={false}
                                                        placeholder="Select Status"
                                                        className="AutoComplete_InputBox"
                                                    />
                                                )}
                                            />
                                        </div>
                                    </Grid>
                                )}
                                <Grid item xs={12} sm={6} mt={1}>
                                    <div className="wsh-form-control">
                                        <Typography className="inputLabel">Priority <span style={{ color: "red" }}>*</span></Typography>
                                        <Autocomplete
                                            options={priorityOptions}
                                            value={
                                                priorityOptions.find(
                                                    (option) => option.value === values.priority
                                                ) || priorityOptions[0] // Default to Low
                                            }
                                            disabled={disabled}
                                            onChange={(e, v) => {
                                                setFieldValue("priority", v?.value);
                                            }}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    focused={false}
                                                    placeholder="Select Priority"
                                                    className="AutoComplete_InputBox"
                                                />
                                            )}
                                        />
                                    </div>
                                </Grid>
                                {(formType === "edit" || formType === "view") && (
                                    <Grid item xs={12} sm={6} mt={1}>
                                        <Typography className="inputLabel">
                                            Priority Level
                                        </Typography>
                                        <Field
                                            name="priorityLevel"
                                            type="number"
                                            className="InputField"
                                            placeholder="Enter Level (e.g. 1)"
                                            disabled
                                        />
                                        <div className="form-error">
                                            {touched.priorityLevel && errors.priorityLevel && errors.priorityLevel}
                                        </div>
                                    </Grid>
                                )}
                            </Grid>
                        </Box>
                        <Divider sx={{ opacity: 1 }} />
                        <Grid className="addboxFooter">
                            <Button
                                className="cancelButton"
                                onClick={() => setOpenDrawer(false)}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                className="saveButton"
                                sx={{ display: disabled ? "none" : "flex" }}
                            >
                                Save
                            </Button>
                        </Grid>
                    </Form>
                )}
            </Formik>
        </div >
    );
};

export default AddTodoTask;
